/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

/**
 * Normalize a hotel name to compare it regardless of case, spacing and apostrophe style
 * @param {String} name - hotel name
 * @returns {String}
 */
const normalizeName = (name) => name.trim().replace(/\s+/g, ' ').replace(/’/g, "'").toLowerCase();

/**
 * Regex source that matches a hotel name regardless of spacing and apostrophe style
 * @param {String} name - hotel name
 * @returns {String}
 */
const toNamePattern = (name) =>
  name
    .trim()
    .split(/\s+/)
    // only the syntax characters are escaped, any other escape is invalid with the unicode flag
    .map((part) => part.replace(/[.*+?^${}()|[\]\\/]/g, '\\$&').replace(/['’]/g, "['’]"))
    .join('\\s+');

/**
 * Create a function that replaces the names in a text with their paired replacements
 * @param {Array} pairs - list of { from, to } name pairs
 * @returns {Function} takes a text and returns it with the names replaced
 */
const createNameReplacer = (pairs) => {
  const labels = new Map();
  pairs.forEach(({ from, to }) => {
    if (typeof from !== 'string' || typeof to !== 'string') return;
    if (!from.trim() || !to.trim()) return;
    const key = normalizeName(from);
    if (key === normalizeName(to) || labels.has(key)) return;
    labels.set(key, { from, to });
  });
  if (labels.size === 0) return (text) => text;

  // longest names first, so that a name is not replaced partially by a shorter name it contains
  const patterns = [...labels.values()]
    .map(({ from }) => from)
    .sort((a, b) => b.length - a.length)
    .map(toNamePattern);
  let regex;
  try {
    regex = new RegExp(`(?<![\\p{L}\\p{N}])(?:${patterns.join('|')})(?![\\p{L}\\p{N}])`, 'giu');
  } catch (err) {
    console.error('Failed to create the hotel name replacer: ', err?.message);
    return (text) => text;
  }

  return (text) => {
    if (typeof text !== 'string' || !text) return text;
    return text.replace(regex, (match) => labels.get(normalizeName(match))?.to ?? match);
  };
};

/**
 * Create a function that replaces the actual hotel names and codes in a text with their demo mode
 * labels
 * @param {Array} hotelList - hotel dropdown list
 * @param {Boolean} isDemoUser - Whether current user is a demo user
 * @returns {Function} takes a text and returns it with the actual hotel names and codes replaced
 */
const createHotelNameAnonymizer = (hotelList, isDemoUser = false) =>
  createNameReplacer(
    (hotelList ?? []).flatMap((hotel) => {
      // the actual name of a hotel is kept in altName for the demo users
      const demoLabel = isDemoUser ? hotel?.name : hotel?.altName;
      const pairs = [{ from: isDemoUser ? hotel?.altName : hotel?.name, to: demoLabel }];
      // codes without a letter are skipped, they would replace the numbers in the analysis
      if (typeof hotel?.hotelCode === 'string' && /\p{L}/u.test(hotel.hotelCode)) {
        pairs.push({ from: hotel.hotelCode, to: demoLabel });
      }
      return pairs;
    })
  );

/**
 * Create a function that replaces the demo mode labels in a text with the actual hotel names.
 * Not to be used for the demo users, they must not see the actual hotel names
 * @param {Array} hotelList - hotel dropdown list
 * @returns {Function} takes a text and returns it with the demo mode labels replaced
 */
export const createHotelNameDeanonymizer = (hotelList) =>
  createNameReplacer((hotelList ?? []).map((hotel) => ({ from: hotel?.altName, to: hotel?.name })));

/**
 * Apply a text replacer to every string in a value, including the object keys (table columns can
 * be hotel names). Objects and arrays are copied, not mutated
 * @param {*} value - string, array, plain object or any other value
 * @param {Function} replace - text replacer
 * @returns {*} value with its strings replaced
 */
export const replaceNamesInValue = (value, replace) => {
  if (typeof value === 'string') return replace(value);
  if (Array.isArray(value)) return value.map((item) => replaceNamesInValue(item, replace));
  if (value && Object.getPrototypeOf(value) === Object.prototype) {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [replace(key), replaceNamesInValue(item, replace)])
    );
  }
  return value;
};

export default createHotelNameAnonymizer;
